import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
// services
import { MasterService } from "../../services/master.service";
import { MessageService } from "../../services/message.service";
import { StoreService } from "../../services/store.service";
import * as languageLibrary from '../../services/language'
import { SecurityService } from "src/app/services/security.service";
import { Stat } from "src/app/models/stat.model";
import { AutomationLog } from "src/app/models/automation-log.model";
import moment from "moment";
@Component({
    selector: "app-dashboard",
    templateUrl: "./dashboard.component.html",
    styleUrls: ["./dashboard.component.scss"],
    standalone: false
})
export class DashboardComponent implements OnInit {
    /** Currently selected lot. */
    protected lotId: number = 0

    /** Indicates the ddashboard data is loading. */
    protected loading: boolean = false

    /** Currently selected language. */
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    /** The language library to lookup static text and display it in the proper language. */
    protected words = languageLibrary.language

    /** The latest daily stat entry for the selected lot. */
    protected latest: Stat | null = null

    /** Last four weeks (grouped by day) stats for the selected lot. */
    protected weeks: Array<Stat> | null = null

    /** Last twelve months (grouped by month) stats for the selected lot. */
    protected months: Array<Stat> | null = null

    constructor(private master: MasterService, private ms: MessageService, private store: StoreService, private router: Router, protected security: SecurityService) { }

    ngOnInit() {
        this.lotId = parseInt(localStorage.getItem('lot') || '0')
        this.fetchWeeklyStats()
        this.fetchMonthlyStats()
    }

    /** Get a list of stats for the last four weeks, grouped by day. */
    private fetchWeeklyStats = () => {
        if (this.security.notPermitted('reports', 'past due analysis', 'view')) {
            return
        }

        if (!this.lotId || (this.lotId <= 0)) {
            return
        }

        const from = moment().startOf('day').subtract(28, 'days').format('YYYY-MM-DD')

        if (this.weeks) {
            this.weeks.length = 0
            this.weeks = null
        }
        
        this.weeks = []

        this.master.get(`insight/stats?pi=1&ipp=28&span=day&lot=${this.lotId}&from=${from}`, (res) => {
            this.loading = false
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data || !res.data.stats || (res.data.stats.length <= 0)) {
                return
            }

            this.weeks = res.data.stats
            this.latest = this.weeks[this.weeks.length-1]
        })
    }
    
    /** Get a list of stats for the last four weeks, grouped by day. */
    private fetchMonthlyStats = () => {
        if (this.security.notPermitted('reports', 'past due analysis', 'view')) {
            return
        }

        if (!this.lotId || (this.lotId <= 0)) {
            return
        }

        const from = moment().startOf('month').subtract(1, 'year').format('YYYY-MM-01')

        if (this.months) {
            this.months.length = 0
            this.months = null
        }
        
        this.months = []

        this.master.get(`insight/stats?pi=1&ipp=28&span=month&lot=${this.lotId}&from=${from}`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data || !res.data.stats || (res.data.stats.length <= 0)) {
                return
            }

            this.months = res.data.stats
        })
    }

    //  create a new vehicle
    protected addVehicle = (): void => {
        if (!this.lotId || (this.lotId <= 0)) {
            return
        }

        const lotId = this.lotId
        if (isNaN(lotId) || (lotId <= 0)) {
            return
        }

        this.router.navigate([`/inventory/add`], { queryParams: { 'lot': lotId } })
    }

    //  create a new sale
    protected addSale = (): void => {
        if (!this.lotId || (this.lotId <= 0)) {
            return
        }

        const lotId = this.lotId
        if (isNaN(lotId) || (lotId <= 0)) {
            return
        }

        let type = 'outside'
        const lot = this.store.userAccount['user'].lots.find(el => el['id'] == this.lotId)
        if (lot) {
            type = 'bhph'
        }

        this.router.navigate([`/sales/add`])
    }

    // todo: listen to output of navbar
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'changeLot':
                const id = parseInt(e['lot'])

                if (this.lotId == id) {
                    return
                }

                this.lotId = id
                this.fetchWeeklyStats()
                this.fetchMonthlyStats()
                break
            case 'setPermissions':
                if (this.latest !== null) {
                    return
                }

                this.fetchWeeklyStats()
                this.fetchMonthlyStats()
                break
        }
    }
}


/**
Cash Flow is the most critical metric for evaluating a Buy Here Pay Here (BHPH) portfolio's performance.  Unlike traditional lending, BHPH success hinges on consistent cash inflows rather than perfect payment history. The primary focus should be on collection potential—the total amount of payments due each week based on contractual terms—and recency, which should ideally remain at 92–96% (meaning 92–96% of open accounts made a payment in the last 30 days). 

To maximize returns:

Target collecting 110% of total payment dollars due weekly while maintaining recency goals. 
Track net static pool rates, loss/liquidation rates, and default rates to benchmark performance against industry standards (e.g., NABD benchmarks). 
Use portfolio migration analysis (roll rates, transition matrices) to predict cash flow and identify early warning signs of deterioration. 
Key Insight: A BHPH portfolio’s value isn’t determined by how many accounts are current, but by how much cash is flowing through the payment window. Even with delinquencies, if total collections exceed scheduled payments and all accounts paid something this month, the portfolio is performing well. 
 */
